// localStorage utility for persisting admin settings
// Backend integration point: replace with API calls to your backend

export interface IqomahSettings {
  subuh: number;    // minutes after adzan
  dzuhur: number;
  ashar: number;
  maghrib: number;
  isya: number;
}

export interface RunningTextItem {
  id: string;
  text: string;
  active: boolean;
  createdAt: string;
}

export interface PhotoItem {
  id: string;
  url: string;
  name: string;
  uploadedAt: string;
}

export type ThemeId = 'dark-green' | 'dark-blue' | 'dark-gold' | 'dark-teal';

export interface AdminSettings {
  iqomah: IqomahSettings;
  runningTexts: RunningTextItem[];
  photos: PhotoItem[];
  selectedTheme: ThemeId;
}

const DEFAULT_SETTINGS: AdminSettings = {
  iqomah: {
    subuh: 10,
    dzuhur: 15,
    ashar: 10,
    maghrib: 8,
    isya: 10,
  },
  runningTexts: [
    {
      id: 'rt-001',
      text: '🕌 Selamat datang di Masjid Baitul Gufron SMK Negeri 5 Malang — Mari kita jaga kebersihan dan kekhusyukan masjid',
      active: true,
      createdAt: '2026-05-20T08:00:00',
    },
    {
      id: 'rt-002',
      text: '📚 Kajian Rutin Setiap Ahad Pagi pukul 07.00 WIB bersama Ustadz Ahmad Fauzi — Tema: Fiqih Sholat Berjamaah',
      active: true,
      createdAt: '2026-05-21T09:00:00',
    },
    {
      id: 'rt-003',
      text: '💧 Program Infaq Pembangunan Masjid — Target Rp 500.000.000 — Rekening BRI: 1234-5678-9012-3456 a.n. Masjid Baitul Gufron',
      active: true,
      createdAt: '2026-05-22T10:00:00',
    },
    {
      id: 'rt-004',
      text: '🌙 Pengajian Malam Jumat pukul 19.30 WIB — Terbuka untuk seluruh warga SMK Negeri 5 Malang dan sekitarnya',
      active: true,
      createdAt: '2026-05-23T11:00:00',
    },
    {
      id: 'rt-005',
      text: '📣 Panitia Qurban Idul Adha 1447 H telah dibuka — Hubungi Takmir Masjid untuk informasi pendaftaran',
      active: false,
      createdAt: '2026-05-24T12:00:00',
    },
  ],
  photos: [
    {
      id: 'photo-001',
      url: 'https://images.unsplash.com/photo-1564769662533-4f00a87b4056?w=1920&q=80',
      name: 'Masjid Eksterior 1',
      uploadedAt: '2026-05-20T08:00:00',
    },
    {
      id: 'photo-002',
      url: 'https://images.unsplash.com/photo-1542816417-0983c9c9ad53?w=1920&q=80',
      name: 'Interior Masjid',
      uploadedAt: '2026-05-21T09:00:00',
    },
    {
      id: 'photo-003',
      url: 'https://images.unsplash.com/photo-1519817650390-64a93db51149?w=1920&q=80',
      name: 'Sholat Berjamaah',
      uploadedAt: '2026-05-22T10:00:00',
    },
  ],
  selectedTheme: 'dark-green',
};

const STORAGE_KEY = 'baitulgufron_admin_settings';

export function getSettings(): AdminSettings {
  if (typeof window === 'undefined') return DEFAULT_SETTINGS;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(stored) as AdminSettings;
    return { ...DEFAULT_SETTINGS, ...parsed };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function saveSettings(settings: AdminSettings): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

export function isAdminLoggedIn(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('baitulgufron_admin_auth') === 'true';
}

export function setAdminLoggedIn(value: boolean): void {
  if (typeof window === 'undefined') return;
  if (value) {
    localStorage.setItem('baitulgufron_admin_auth', 'true');
  } else {
    localStorage.removeItem('baitulgufron_admin_auth');
  }
}

export const THEMES: Record<ThemeId, { label: string; bg: string; accent: string; card: string; description: string }> = {
  'dark-green': {
    label: 'Hijau Islami',
    bg: '#0a1f12',
    accent: '#d4a853',
    card: '#0d2818',
    description: 'Tema hijau gelap dengan aksen emas — klasik dan elegan',
  },
  'dark-blue': {
    label: 'Biru Malam',
    bg: '#0a0f1f',
    accent: '#60a5fa',
    card: '#0d1428',
    description: 'Tema biru tua dengan aksen biru muda — tenang dan modern',
  },
  'dark-gold': {
    label: 'Emas Kerajaan',
    bg: '#1a1200',
    accent: '#fbbf24',
    card: '#261c00',
    description: 'Tema hitam keemasan — mewah dan berkesan',
  },
  'dark-teal': {
    label: 'Teal Sejuk',
    bg: '#071a1a',
    accent: '#2dd4bf',
    card: '#0a2424',
    description: 'Tema teal gelap dengan aksen cyan — segar dan kontemporer',
  },
};